import React, { useEffect } from 'react';
import { IonicButton, IonicInput } from 'ionic-ui';
import { Controller, useFormContext } from 'react-hook-form';
import z from 'zod';
import { signupSchema } from '../../../../schemas/signupSchema';
import { useDialog } from '../../../../context/Dialog';
import {
  sendVerificationCodeToPhone,
  verifyUser,
  verifyUserPhone,
} from '../../../../services/authentication/signup';
import { updateTemporalSession } from '../../../../utils/tempSession';
import useIonicToast from '../../../../hooks/useIonicToast';
import useEmailVerificationServices from '../../../../services/authentication/hooks/useEmailVerification';
import { useDispatch } from 'react-redux';
import { setUserSession } from '../../../../store/slices/UserSession/userSession';
import { setUserProfile } from '../../../../store/slices/userProfile/userProfileSlice';
import { baseURL } from '../../../../data/config';

async function recordTermsAcceptance(accessToken: string): Promise<void> {
  try {
    await fetch(`${baseURL}/auth/terms/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ audience: 'store' }),
    });
  } catch {
    // swallow — gate will catch the user on next login
  }
}

type Props = {
  useForm: ReturnType<typeof useFormContext<z.infer<typeof signupSchema>>>;
  onNext: () => void;
  verificationMethod?: 'email' | 'sms' | 'whatsapp';
};

export default function EmailVerificationDialog({
  useForm,
  onNext,
  verificationMethod = 'email',
}: Props) {
  const { control, getValues: getFormValues, trigger } = useForm;
  const { closeDialog, setBusy } = useDialog();
  const { presentIonToast } = useIonicToast();
  const dispatch = useDispatch();
  const {
    counterTime,
    isCounting,
    verifyWithEmail: sendToken,
    startCountDown,
  } = useEmailVerificationServices();
  const hasAutoRequestedRef = React.useRef(false);

  //THIS SENDS A TOKEN TO USER'S EMAIL
  const sendVerificationCode = async () => {
    try {
      setBusy(true);
      const { email, phone } = getFormValues();
      if (verificationMethod === 'email') {
        await sendToken(email);
      } else if (phone) {
        await sendVerificationCodeToPhone(phone, verificationMethod);
        startCountDown();
      } else {
        throw new Error('Phone number is required for phone verification');
      }
    } catch (err: any) {
      // eslint-disable-line
      presentIonToast(err.message as string, 'error');
    } finally {
      setBusy(false);
    }
  };

  //verifies token value user enters and sends email verification to the backend
  const verifyCode = async () => {
    const isValid = await trigger(['token']);
    const { email, phone, token } = getFormValues();

    if (!isValid) return;
    try {
      setBusy(true);
      const res =
        verificationMethod === 'email'
          ? await verifyUser({ email, token })
          : await verifyUserPhone({
              phone,
              token,
              via: verificationMethod,
            });
      const { signedAccessToken, signedRefreshPayload, profile } = res;

      // A genuine verification MUST return an access token. Without one,
      // dispatching a session leaves the app "logged in" with a null token and
      // every request fails. Surface the message instead of a silent break.
      if (!signedAccessToken) {
        const resInfo = res as { details?: string; message?: string };
        presentIonToast(
          resInfo?.details ||
            resInfo?.message ||
            'Unable to complete verification. Please try again.',
          'error'
        );
        return;
      }

      dispatch(
        setUserSession({
          signedAccessToken,
          signedRefreshPayload,
        })
      );
      dispatch(setUserProfile(profile));
      updateTemporalSession({ isVerified: true });

      // Record the T&C acceptance the user gave on signup so the post-login
      // gate doesn't re-prompt. Best-effort — failure here just means the
      // user re-confirms on first dashboard load.
      if (signedAccessToken) {
        void recordTermsAcceptance(signedAccessToken);
      }

      presentIonToast('Account creation successful');
      closeDialog();
      onNext();
    } catch (error: any) {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      let msg: string = error.message;
      if (error.status === 401) {
        msg = 'Token is invalid or has expired';
      }
      presentIonToast(msg, 'error');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (isCounting || hasAutoRequestedRef.current) return;
    hasAutoRequestedRef.current = true;
    void sendVerificationCode();
  }, []);

  const title =
    verificationMethod === 'email'
      ? 'Verify your Email'
      : verificationMethod === 'sms'
        ? 'Verify your Phone (SMS)'
        : 'Verify your Phone (WhatsApp)';

  const description =
    verificationMethod === 'email'
      ? "We've sent you a verification code. Please check your email to verify your account."
      : verificationMethod === 'sms'
        ? "We've sent a verification code via SMS. Enter the code to verify your phone."
        : "We've sent a verification code via WhatsApp. Enter the code to verify your phone.";

  return (
    <div className="dialog-content space-y-4 sm:w-80">
      <div className="rounded-md bg-[#d2ebfc]">
        <img
          src="/images/mail-envelope.png"
          alt="OTP Code"
          className="max-h-40 mx-auto"
        />
      </div>
      <h5 className="high-emphasis-text font-bold text-center">{title}</h5>
      <p className="medium-emphasis-text text-center">{description}</p>
      <Controller
        control={control}
        name="token"
        render={({ field, fieldState }) => (
          <IonicInput
            {...field}
            name="token"
            containerClass="max-w-44 mx-auto"
            placeholder="******"
            className="text-xl text-center tracking-[10px] max-w-full"
            maxLength={6}
            hint={fieldState?.error?.message}
            hintClass="text-red-600 dark:text-red-600 text-center"
          />
        )}
      />

      <div className="flex gap-2">
        <IonicButton onClick={verifyCode} className="w-1/2">
          Proceed
        </IonicButton>
        <IonicButton
          className={`w-1/2 text-primary text-nowrap disabled:text-text-dark-high`}
          variant="outline"
          disabled={isCounting}
          onClick={sendVerificationCode}
        >
          {!isCounting ? 'Send Token' : `Resend Code in ${counterTime}`}
        </IonicButton>
      </div>
    </div>
  );
}
